import * as XLSX from 'xlsx';
import { loadResultsFromS3, hasJSONStyleResults, getSummaryFindingsTable, formatHeader } from './Utility';
import { VIEW_OPTION_IDS, EXCEPTION_STATUSES } from './Constants';

/**
 * Downloads individual FirstPass results as an Excel file
 * @param {object} data - The data object with response_s3_path, entity_name, current_year
 * @param {function} onStart - Callback when download starts
 * @param {function} onComplete - Callback when download completes
 * @param {string} viewOption - The current view option ('exception' or 'all')
 */
export const downloadFirstPassExcel = async (data, onStart, onComplete, viewOption) => {
  if (onStart) onStart();

  try {
    const s3Data = await loadResultsFromS3(data);

    if (!s3Data) {
      console.error('No result content found');
      if (onComplete) onComplete();
      return;
    }

    const wb = XLSX.utils.book_new();
    const fileName = `${data.entity_name || 'FirstPass'}_${data.current_year || ''}.xlsx`;

    if (!hasJSONStyleResults(s3Data)) {
      // Markdown-style results - single sheet with raw content
      const sheetData = [['Result']];
      if (s3Data?.status_report?.result) {
        sheetData.push([s3Data.status_report.result]);
      }
      if (s3Data?.analysis) {
        sheetData.push(['']);
        sheetData.push(['Analysis']);
        sheetData.push([s3Data.analysis]);
      }
      if (s3Data?.checks?.length > 0) {
        for (const check of s3Data.checks) {
          sheetData.push(['']);
          sheetData.push([check.check_name || 'Check']);
          sheetData.push([check.analysis || '']);
        }
      }
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
    } else if (viewOption === VIEW_OPTION_IDS.EXCEPTION) {
      // Exception view - single sheet with exception findings
      buildExceptionSheet(wb, s3Data);
    } else {
      // All view - separate sheet for each section
      buildAllViewSheets(wb, s3Data);
    }

    // Guard against empty workbook (e.g. no status_report and no checks)
    if (wb.SheetNames.length === 0) {
      const ws = XLSX.utils.aoa_to_sheet([['No results available.']]);
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
    }

    XLSX.writeFile(wb, fileName);
  } catch (error) {
    console.error('Error downloading Excel:', error);
  }

  if (onComplete) onComplete();
};

/**
 * Builds a single sheet with exception findings
 */
const buildExceptionSheet = (wb, s3Data) => {
  const summaryFindings = getSummaryFindingsTable(s3Data);
  const totalFyiFromChecks = (s3Data?.checks || []).reduce((total, check) => {
    return total + (check?.analysis?.summary?.fyi_count || 0);
  }, 0);

  // Summary info
  const summaryData = [];
  if (s3Data?.status_report?.result) {
    const result = s3Data.status_report.result;
    if (typeof result === 'object' && result.total_sections !== undefined) {
      summaryData.push(['Total Sections', 'Sections With Issues', 'Total Important Findings', 'Total FYI Findings']);
      summaryData.push([result.total_sections, result.sections_with_issues, result.total_important_findings, totalFyiFromChecks]);
      summaryData.push([]);
    }
  }

  if (summaryFindings && summaryFindings.length > 0) {
    summaryData.push(['Exception Findings']);
    const columns = Object.keys(summaryFindings[0]);
    summaryData.push(columns.map(col => formatHeader(col)));
    for (const row of summaryFindings) {
      summaryData.push(columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : ''));
    }
  } else {
    summaryData.push(['No exceptions found.']);
  }

  const ws = XLSX.utils.aoa_to_sheet(summaryData);
  autoFitColumns(ws, summaryData);
  XLSX.utils.book_append_sheet(wb, ws, 'Exceptions');
};

/**
 * Builds multiple sheets for the "all" view - one sheet per section/check
 */
const buildAllViewSheets = (wb, s3Data) => {
  const usedNames = new Set();
  const totalFyiFromChecks = (s3Data?.checks || []).reduce((total, check) => {
    return total + (check?.analysis?.summary?.fyi_count || 0);
  }, 0);

  // Status Report sheet
  if (s3Data?.status_report?.result) {
    const result = s3Data.status_report.result;
    const statusData = [];

    // Summary metrics
    if (typeof result === 'object' && result.total_sections !== undefined) {
      statusData.push(['Total Sections', 'Sections With Issues', 'Total Important Findings', 'Total FYI Findings']);
      statusData.push([result.total_sections, result.sections_with_issues, result.total_important_findings, totalFyiFromChecks]);
      statusData.push([]);
    }

    // Summary
    if (result?.summary) {
      statusData.push(['Summary']);
      const summaryKeys = Object.keys(result.summary);
      statusData.push(summaryKeys.map(k => formatHeader(k)));
      statusData.push(summaryKeys.map(k => result.summary[k] !== null && result.summary[k] !== undefined ? result.summary[k] : ''));
      statusData.push([]);
    }

    // Findings text
    if (result?.findings?.text) {
      statusData.push(['Findings Summary']);
      statusData.push([result.findings.text]);
      statusData.push([]);
    }

    // Findings table
    if (result?.findings?.table && result.findings.table.length > 0) {
      statusData.push(['Overall Findings']);
      const columns = Object.keys(result.findings.table[0]);
      statusData.push(columns.map(col => formatHeader(col)));
      for (const row of result.findings.table) {
        statusData.push(columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : ''));
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(statusData);
    autoFitColumns(ws, statusData);
    XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName('Status Report', usedNames));
  }

  // Individual check sheets
  if (s3Data?.checks?.length > 0) {
    for (const check of s3Data.checks) {
      const checkData = [];
      const checkName = check.check_name || 'Check';

      // Check summary
      if (check?.analysis?.summary) {
        const summary = check.analysis.summary;
        checkData.push(['Summary']);
        const summaryKeys = Object.keys(summary);
        checkData.push(summaryKeys.map(k => formatHeader(k)));
        checkData.push(summaryKeys.map(k => summary[k] !== null && summary[k] !== undefined ? summary[k] : ''));
        checkData.push([]);
      }

      // Findings text
      if (check?.analysis?.findings?.text) {
        checkData.push(['Findings Summary']);
        checkData.push([check.analysis.findings.text]);
        checkData.push([]);
      }

      // Findings table
      if (check?.analysis?.findings?.table && check.analysis.findings.table.length > 0) {
        checkData.push(['Findings']);
        const columns = Object.keys(check.analysis.findings.table[0]);
        checkData.push(columns.map(col => formatHeader(col)));
        for (const row of check.analysis.findings.table) {
          checkData.push(columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : ''));
        }
      }

      // Fallback: if analysis is a string
      if (typeof check.analysis === 'string') {
        checkData.push([check.analysis]);
      }

      if (checkData.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet(checkData);
        autoFitColumns(ws, checkData);
        XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(checkName, usedNames));
      }
    }
  }
};

/**
 * Downloads consolidated results as an Excel file
 * @param {Array} allFindings - Array of consolidated findings
 * @param {object} stats - Stats object with totalEntities, totalImportant, totalFyi
 * @param {string} label - Report label/name
 * @param {function} onComplete - Callback when done
 */
export const downloadConsolidatedExcel = (allFindings, stats, label, onComplete) => {
  try {
    const wb = XLSX.utils.book_new();
    const fileName = `${label || 'Consolidated_Report'}.xlsx`;

    const sheetData = [];

    // Summary row
    sheetData.push(['Consolidated Exception Report']);
    sheetData.push([]);
    sheetData.push(['Entities Included', 'Total Important Findings', 'Total FYI Findings']);
    sheetData.push([stats.totalEntities, stats.totalImportant, stats.totalFyi]);
    sheetData.push([]);

    if (allFindings && allFindings.length > 0) {
      sheetData.push(['Exception Findings']);
      const columns = ['no', 'section', 'team', 'entity_code', 'entity_name', 'severity', 'description', 'location', 'current_value', 'expected_value', 'impact', 'action_required'];
      sheetData.push(columns.map(col => formatHeader(col)));
      for (const row of allFindings) {
        sheetData.push(columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : ''));
      }
    } else {
      sheetData.push(['No exceptions found across the selected entities.']);
    }

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    autoFitColumns(ws, sheetData);
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidated Exceptions');

    XLSX.writeFile(wb, fileName);
  } catch (error) {
    console.error('Error downloading consolidated Excel:', error);
  }

  if (onComplete) onComplete();
};

/**
 * Downloads all history results for a consolidated report as Excel (one sheet per entity)
 * @param {Array} entityDetails - Array of entity detail objects
 * @param {object} resultsPerRequestId - Map of request_id to history items
 * @param {string} label - Report label
 * @param {function} onComplete - Callback when done
 */
export const downloadConsolidatedHistoryExcel = async (entityDetails, resultsPerRequestId, label, onComplete) => {
  try {
    const wb = XLSX.utils.book_new();
    const fileName = `${label || 'Consolidated_History'}.xlsx`;

    // Load all entity S3 data concurrently for performance
    let allFindings = [];
    const entitySheetEntries = [];

    const results = await Promise.all(
      entityDetails.map(async (detail) => {
        const historyItem = resultsPerRequestId[detail.request_id];
        if (!historyItem || !historyItem.response_s3_path) return null;
        try {
          return { detail, s3Data: await loadResultsFromS3(historyItem) };
        } catch (err) {
          console.error(`Error loading results for ${detail.entity_name}:`, err);
          return null;
        }
      })
    );

    for (const item of results) {
      if (!item) continue;
      const { detail, s3Data } = item;
      const summaryFindings = getSummaryFindingsTable(s3Data);

      if (summaryFindings && summaryFindings.length > 0) {
        // Filter to only exception-level findings (consistent with PDF output)
        const exceptionFindings = summaryFindings.filter(
          finding => EXCEPTION_STATUSES.includes(finding.severity)
        );

        const entityFindings = exceptionFindings.map(finding => ({
          section: finding.section || '',
          team: detail.team || '',
          entity_code: detail.entity_code || '',
          entity_name: detail.entity_name || '',
          ...finding,
        }));
        allFindings.push(...entityFindings);

        // Store per-entity sheet data (filtered to exceptions only)
        if (exceptionFindings.length > 0) {
          entitySheetEntries.push({
            displayName: detail.entity_name || detail.entity_code || 'Entity',
            findings: exceptionFindings,
          });
        }
      }
    }

    // Consolidated sheet
    const consolidatedData = [];
    consolidatedData.push(['Consolidated Exception Report']);
    consolidatedData.push([]);
    consolidatedData.push(['Entities Included', 'Total Findings']);
    consolidatedData.push([entityDetails.length, allFindings.length]);
    consolidatedData.push([]);

    if (allFindings.length > 0) {
      // Sort and number
      allFindings.sort((a, b) => {
        const sectionA = (a.section || '').toLowerCase();
        const sectionB = (b.section || '').toLowerCase();
        return sectionA < sectionB ? -1 : sectionA > sectionB ? 1 : 0;
      });
      allFindings = allFindings.map((f, i) => ({ ...f, no: i + 1 }));

      const columns = ['no', 'section', 'team', 'entity_code', 'entity_name', 'severity', 'description', 'location', 'current_value', 'expected_value', 'impact', 'action_required'];
      consolidatedData.push(columns.map(col => formatHeader(col)));
      for (const row of allFindings) {
        consolidatedData.push(columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : ''));
      }
    }

    const usedNames = new Set();
    const wsConsolidated = XLSX.utils.aoa_to_sheet(consolidatedData);
    autoFitColumns(wsConsolidated, consolidatedData);
    XLSX.utils.book_append_sheet(wb, wsConsolidated, sanitizeSheetName('All Findings', usedNames));

    // Per-entity sheets (using array to preserve order and handle duplicate names)
    for (const entry of entitySheetEntries) {
      if (entry.findings.length === 0) continue;
      const sheetData = [];
      const columns = Object.keys(entry.findings[0]);
      sheetData.push(columns.map(col => formatHeader(col)));
      for (const row of entry.findings) {
        sheetData.push(columns.map(col => row[col] !== null && row[col] !== undefined ? row[col] : ''));
      }
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      autoFitColumns(ws, sheetData);
      XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(entry.displayName, usedNames));
    }

    XLSX.writeFile(wb, fileName);
  } catch (error) {
    console.error('Error downloading consolidated history Excel:', error);
  }

  if (onComplete) onComplete();
};

/**
 * Sanitizes a sheet name to be valid for Excel (max 31 chars, no special chars).
 * Tracks used names to prevent duplicates by appending a counter suffix.
 * @param {string} name - The desired sheet name
 * @param {Set} usedNames - Set of already-used sheet names (mutated in place)
 * @returns {string} A unique, valid sheet name
 */
const sanitizeSheetName = (name, usedNames = new Set()) => {
  if (!name) name = 'Sheet';
  // Remove invalid characters: \ / ? * [ ]
  let sanitized = name.replace(/[\\/?*[\]]/g, '');
  // Truncate to 31 characters (Excel limit)
  if (sanitized.length > 31) {
    sanitized = sanitized.substring(0, 31);
  }
  if (!sanitized) sanitized = 'Sheet';

  // De-duplicate: if name already used, append counter suffix
  let finalName = sanitized;
  let counter = 2;
  while (usedNames.has(finalName)) {
    const suffix = ` (${counter})`;
    const maxBaseLen = 31 - suffix.length;
    finalName = sanitized.substring(0, maxBaseLen) + suffix;
    counter++;
  }
  usedNames.add(finalName);
  return finalName;
};

/**
 * Auto-fit column widths based on content
 */
const autoFitColumns = (ws, data) => {
  if (!data || data.length === 0) return;
  const colWidths = [];
  for (const row of data) {
    for (let i = 0; i < row.length; i++) {
      const cellValue = String(row[i] || '');
      const width = Math.min(Math.max(cellValue.length + 2, 10), 50);
      if (!colWidths[i] || width > colWidths[i]) {
        colWidths[i] = width;
      }
    }
  }
  ws['!cols'] = colWidths.map(w => ({ wch: w }));
};
